import { Button, Input, Label, Select, Icon, Text } from '@nimbus-ds/components'
import { PlusIcon, TrashIcon, SlidersIcon } from '@nimbus-ds/icons'

export interface VariantEntry {
  name: string
  trafficPct: number
}

interface MultiVariantSplitProps {
  variants: Record<string, VariantEntry>
  onChange: (variants: Record<string, VariantEntry>) => void
  maxVariants?: number
}

const VARIANT_KEYS = ['variant_b', 'variant_c', 'variant_d', 'variant_e']
const VARIANT_COLORS = ['border-blue-200 bg-blue-50', 'border-purple-200 bg-purple-50', 'border-amber-200 bg-amber-50', 'border-emerald-200 bg-emerald-50']

function getDefaultVariants(count: number): Record<string, VariantEntry> {
  const variants: Record<string, VariantEntry> = {
    control: { name: 'Control', trafficPct: Math.ceil(100 / (count + 1)) },
  }
  const remaining = 100 - variants.control.trafficPct
  const perVariant = Math.floor(remaining / count)
  for (let i = 0; i < count; i++) {
    const isLast = i === count - 1
    variants[VARIANT_KEYS[i]] = {
      name: `Variante ${String.fromCharCode(66 + i)}`, // B, C, D, E
      trafficPct: isLast ? remaining - perVariant * (count - 1) : perVariant,
    }
  }
  return variants
}

export function MultiVariantSplit({
  variants,
  onChange,
  maxVariants = 5,
}: MultiVariantSplitProps) {
  const variantKeys = Object.keys(variants).filter(k => k !== 'control')
  const treatmentCount = variantKeys.length
  const totalPct = Object.values(variants).reduce((sum, v) => sum + v.trafficPct, 0)
  const isValid = totalPct === 100

  const handleCountChange = (count: string) => {
    const n = parseInt(count)
    onChange(getDefaultVariants(n))
  }

  const handleTrafficChange = (key: string, value: number) => {
    onChange({ ...variants, [key]: { ...variants[key], trafficPct: value } })
  }

  const handleNameChange = (key: string, name: string) => {
    onChange({ ...variants, [key]: { ...variants[key], name } })
  }

  const handleEqualSplit = () => {
    const total = Object.keys(variants).length
    const base = Math.floor(100 / total)
    const remainder = 100 - base * total
    const updated: Record<string, VariantEntry> = {}
    const keys = Object.keys(variants)
    keys.forEach((key, i) => {
      updated[key] = {
        ...variants[key],
        trafficPct: base + (i < remainder ? 1 : 0),
      }
    })
    onChange(updated)
  }

  const handleAddVariant = () => {
    if (treatmentCount >= maxVariants - 1) return
    const newKey = VARIANT_KEYS[treatmentCount]
    const newName = `Variante ${String.fromCharCode(66 + treatmentCount)}`
    onChange({
      ...variants,
      [newKey]: { name: newName, trafficPct: 0 },
    })
  }

  const handleRemoveVariant = (key: string) => {
    if (treatmentCount <= 1) return
    const { [key]: _, ...rest } = variants
    onChange(rest)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Configuración de Variantes</Label>
        <div className="w-[200px]">
          <Select
            id="variant-count"
            name="variant-count"
            value={String(treatmentCount)}
            onChange={(e) => handleCountChange(e.target.value)}
          >
            {[1, 2, 3, 4].map(n => (
              <Select.Option key={n} value={String(n)} label={`Control + ${n} variante${n > 1 ? 's' : ''}`} />
            ))}
          </Select>
        </div>
      </div>

      {/* Variant rows — compact colored boxes (the tint/border encodes the variant) */}
      <div className="space-y-2">
        {/* Control */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <Text fontWeight="bold" color="neutral-textHigh">Control (A)</Text>
            <div className="flex shrink-0 items-center gap-1.5">
              <div className="w-16 shrink-0">
                <Input
                  type="number"
                  min={5}
                  max={90}
                  value={variants.control?.trafficPct ?? 50}
                  onChange={(e) => handleTrafficChange('control', parseInt(e.target.value) || 0)}
                />
              </div>
              <Text fontSize="caption" as="span">%</Text>
            </div>
          </div>
          <Text fontSize="caption">
            La experiencia actual que ven tus visitantes
          </Text>
        </div>

        {/* Treatment variants */}
        {variantKeys.map((key, i) => (
          <div key={key} className={`rounded-lg border px-3 py-2 ${VARIANT_COLORS[i] || 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <Input
                  value={variants[key].name}
                  onChange={(e) => handleNameChange(key, e.target.value)}
                />
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <div className="w-16 shrink-0">
                  <Input
                    type="number"
                    min={5}
                    max={90}
                    value={variants[key].trafficPct}
                    onChange={(e) => handleTrafficChange(key, parseInt(e.target.value) || 0)}
                  />
                </div>
                <Text fontSize="caption" as="span">%</Text>
                {treatmentCount > 1 && (
                  <Button
                    appearance="transparent"
                    onClick={() => handleRemoveVariant(key)}
                  >
                    <Icon source={<TrashIcon />} color="currentColor" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {treatmentCount < maxVariants - 1 && (
          <Button appearance="neutral" onClick={handleAddVariant}>
            <Icon source={<PlusIcon />} color="currentColor" />
            Agregar variante
          </Button>
        )}
        <Button appearance="neutral" onClick={handleEqualSplit}>
          <Icon source={<SlidersIcon />} color="currentColor" />
          Distribución igual
        </Button>
      </div>

      {/* Validation */}
      {!isValid && (
        <p className="text-sm text-destructive">
          El total debe sumar 100% (actual: {totalPct}%)
        </p>
      )}
    </div>
  )
}
